import React, { useState, useEffect } from 'react';
import { callApi, callPublicApi } from '../api';
import EtkinlikFormu from '../components/EtkinlikFormu';
import './YonetimSayfasi.css';
import { MapPin, CalendarDays, Edit3, Trash2, BellRing, Eye, EyeOff, RotateCcw, ImageOff, UserX } from 'lucide-react';

const EtkinlikYonetim = ({ kulup }) => {
    const [etkinlikler, setEtkinlikler] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [etkinlikToEdit, setEtkinlikToEdit] = useState(null);

    useEffect(() => {
        fetchEtkinlikler();
    }, [kulup]); // Kulüp değişirse yeniden çek

    const formatDate = (dateString) => {
        if (!dateString) return 'Belirtilmemiş';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Geçersiz Tarih';
        return date.toLocaleDateString('tr-TR', {
            year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const fetchEtkinlikler = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await callPublicApi('/etkinlikler');

            let parsedBody = [];
            // Hem eski (body string) hem yeni (direkt array) formatı destekle
            if (data && data.body) {
                parsedBody = JSON.parse(data.body);
            } else {
                parsedBody = data;
            }

            let etkinlikItems = Array.isArray(parsedBody) ? parsedBody : parsedBody.items || [];

            // KULÜP FILTRELEME
            if (kulup) {
                etkinlikItems = etkinlikItems.filter(item => {
                    // Eğer etkinlikte kulupID yoksa (eski veri) ve şu anki kulüp 'Default' ise göster
                    // VEYA item.kulupID eşleşiyorsa göster
                    // Gastronomi (Default) için: ID'si olmayanları da sahiplen
                    if (kulup.kulupID === 'klugastro' || !kulup.kulupID) {
                        return !item.kulupID || item.kulupID === 'klugastro';
                    }
                    // Diğer kulüpler (Müzik vb) için: Sadece kendi ID'si olanlar
                    return item.kulupID === kulup.kulupID;
                });
            }

            // Sadece 'etkinlikTarihi' alanına göre sıralanıyor
            etkinlikItems.sort((a, b) => new Date(b.etkinlikTarihi) - new Date(a.etkinlikTarihi));

            setEtkinlikler(etkinlikItems);
        } catch (err) {
            console.error("Etkinlik yükleme hatası:", err);
            setError("Etkinlikler yüklenemedi.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (etkinlikID) => {
        if (!window.confirm("Bu etkinliği silmek (çöp kutusuna taşımak) istediğinizden emin misiniz?")) return;
        setIsLoading(true);
        try {
            // Backend DELETE endpoint performs Soft Delete (silindi=true)
            await callApi(`/etkinlikler?etkinlikID=${etkinlikID}`, 'DELETE');
            fetchEtkinlikler();
        } catch (err) {
            setError(err.message || "Silme işlemi başarısız oldu.");
            setIsLoading(false);
        }
    };

    const handleRestore = async (etkinlikID) => {
        if (!window.confirm("Bu etkinliği geri yüklemek istediğinizden emin misiniz?")) return;
        setIsLoading(true);
        try {
            // Restore via PUT (update silindi field)
            await callApi('/etkinlikler', 'PUT', { etkinlikID, silindi: false });
            fetchEtkinlikler();
        } catch (err) {
            setError(err.message || "Geri yükleme başarısız.");
            setIsLoading(false);
        }
    };

    const handleEdit = (etkinlik) => {
        setEtkinlikToEdit(etkinlik);
        setIsFormOpen(true);
    };

    const handleFormSave = () => {
        setIsFormOpen(false);
        setEtkinlikToEdit(null);
        fetchEtkinlikler();
    };

    // Listeleri ayır
    const now = new Date();
    // Filter out deleted items for main lists
    const activeEvents = etkinlikler.filter(e => !e.silindi);
    const deletedEvents = etkinlikler.filter(e => e.silindi);

    const futureEvents = activeEvents.filter(e => new Date(e.etkinlikTarihi) >= now);
    const pastEvents = activeEvents.filter(e => new Date(e.etkinlikTarihi) < now);

    const handleSendReminders = async (etkinlik) => {
        if (!window.confirm(`${etkinlik.baslik} etkinliği için TÜM ONAYLI katılımcılara hatırlatma maili gönderilsin mi?`)) return;
        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}etkinlikler/${etkinlik.etkinlikID}/send-reminders`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
            });
            const data = await response.json();
            if (data.success) {
                alert(data.message);
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            alert("Hata: " + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseNoShow = async (etkinlik) => {
        if (!window.confirm(`"${etkinlik.baslik}" etkinliği için yoklamayı kapatmak ve QR okutmayan (gelmeyen) tüm asil kayıtlı üyelere No-Show cezası uygulamak istediğinize emin misiniz?`)) return;
        setIsLoading(true);
        try {
            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://klugastro-backend.steamdeneme38.workers.dev/';
            const cleanBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
            const response = await fetch(`${cleanBase}etkinlikler/${etkinlik.etkinlikID}/no-show-kapat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`
                }
            });
            const data = await response.json();
            if (data.success) {
                alert(data.message);
                fetchEtkinlikler();
            } else {
                throw new Error(data.error || 'İşlem başarısız.');
            }
        } catch (err) {
            alert("No-Show Hatası: " + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleVisibility = async (etkinlik) => {
        setIsLoading(true);
        try {
            const newStatus = !etkinlik.gizli;
            await callApi(`/etkinlikler${kulup ? `?kulupID=${kulup.kulupID}` : ''}`, 'PUT', {
                etkinlikID: etkinlik.etkinlikID,
                gizli: newStatus
            });
            fetchEtkinlikler();
        } catch (err) {
            setError("Görünürlük değiştirilemedi: " + err.message);
            setIsLoading(false);
        }
    };

    const renderTable = (items, title, isDeleted = false) => (
        <div style={{ marginBottom: '40px' }} key={title}>
            <h3 style={{ borderBottom: '2px solid var(--border-color)', paddingBottom: '10px', color: isDeleted ? 'var(--text-muted)' : 'var(--text-primary)', marginBottom: '20px' }}>
                {title} ({items.length})
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                {items.length > 0 ? (
                    items.map((etkinlik) => (
                        <div key={etkinlik.etkinlikID} style={{ 
                            background: 'var(--bg-card)', borderRadius: '20px', padding: '16px', 
                            border: `1px solid ${isDeleted ? '#fca5a5' : 'var(--border-color)'}`, 
                            boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                            opacity: etkinlik.gizli || isDeleted ? 0.7 : 1,
                            position: 'relative', display: 'flex', flexDirection: 'column'
                        }}>
                            {/* Üst Görsel / Buton */}
                            <div style={{ position: 'relative', width: '100%', height: '140px', borderRadius: '12px', overflow: 'hidden', background: 'var(--bg-secondary)', marginBottom: '16px' }}>
                                {(etkinlik.resimURL || etkinlik.gorselUrl) ? (
                                    <img src={etkinlik.resimURL || etkinlik.gorselUrl} alt="Etkinlik" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                                        <ImageOff size={32} />
                                    </div>
                                )}
                                {!isDeleted && (
                                    <button
                                        onClick={() => toggleVisibility(etkinlik)}
                                        style={{ position: 'absolute', top: '8px', right: '8px', background: 'rgba(255,255,255,0.9)', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}
                                        title={etkinlik.gizli ? "Yayınla" : "Gizle"}
                                    >
                                        {etkinlik.gizli ? <EyeOff size={16} color="#ef4444" /> : <Eye size={16} color="#10b981" />}
                                    </button>
                                )}
                            </div>

                            {/* Detaylar */}
                            <div style={{flex: 1}}>
                                <h4 style={{ margin: '0 0 8px', fontSize: '1.1rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    {etkinlik.baslik}
                                    {Boolean(etkinlik.gizli) && <span style={{ fontSize: '0.65rem', background: '#fee2e2', color: '#ef4444', padding: '2px 6px', borderRadius: '4px' }}>Gizli</span>}
                                </h4>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <MapPin size={14} /> {etkinlik.konum || etkinlik.yer || 'Belirtilmemiş'}
                                    </span>

                                    <span style={{ fontSize: '0.85rem', color: '#10b981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <CalendarDays size={14} /> {formatDate(etkinlik.etkinlikTarihi)}
                                    </span>
                                </div>
                            </div>

                            {/* Aksiyonlar */}
                            <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', borderTop: '1px solid var(--border-light)', paddingTop: '12px', flexWrap: 'wrap' }}>
                                {isDeleted ? (
                                    <button onClick={() => handleRestore(etkinlik.etkinlikID)} style={{ flex: 1, padding: '10px', borderRadius: '10px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', cursor: 'pointer', minHeight: '44px' }}>
                                        <RotateCcw size={16} /> Geri Yükle
                                    </button>
                                ) : (
                                    <>
                                        <button onClick={() => handleSendReminders(etkinlik)} style={{ flex: '1 1 45%', padding: '10px 8px', borderRadius: '10px', background: '#fff7ed', border: '1px solid #ffd8a8', color: '#ea580c', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', cursor: 'pointer', minHeight: '44px', fontSize: '0.78rem', fontWeight: 600 }} title="Hatırlat">
                                            <BellRing size={15} /> Hatırlat
                                        </button>
                                        <button onClick={() => handleCloseNoShow(etkinlik)} style={{ flex: '1 1 45%', padding: '10px 8px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', cursor: 'pointer', minHeight: '44px', fontSize: '0.78rem', fontWeight: 600 }} title="Yoklamayı Kapat">
                                            <UserX size={15} /> No-Show
                                        </button>
                                        <button onClick={() => handleEdit(etkinlik)} style={{ flex: '1 1 45%', padding: '10px 8px', borderRadius: '10px', background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', cursor: 'pointer', minHeight: '44px', fontSize: '0.78rem', fontWeight: 600 }} title="Düzenle">
                                            <Edit3 size={15} /> Düzenle
                                        </button>
                                        <button onClick={() => handleDelete(etkinlik.etkinlikID)} style={{ flex: '1 1 45%', padding: '10px 8px', borderRadius: '10px', background: '#fee2e2', border: '1px solid #fca5a5', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', cursor: 'pointer', minHeight: '44px', fontSize: '0.78rem', fontWeight: 600 }} title="Sil">
                                            <Trash2 size={15} /> Sil
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '30px', background: 'var(--bg-card)', borderRadius: '16px', color: 'var(--text-muted)' }}>
                        Bu kategoride etkinlik yok.
                    </div>
                )}
            </div>
        </div>
    );

    if (isLoading && etkinlikler.length === 0) return <div className="loading-state">Etkinlikler Yükleniyor...</div>;

    return (
        <div className="yonetim-sayfasi">
            <h2>Etkinlik Yönetimi {kulup ? `(${kulup.ad})` : ''} <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>v2.1 (Fix)</span></h2>

            {error && <div className="error-message">{error}</div>}

            <button className="primary-button" onClick={() => { setEtkinlikToEdit(null); setIsFormOpen(true); }} style={{ marginBottom: '20px' }}>
                Yeni Etkinlik Ekle
            </button>

            {isFormOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <EtkinlikFormu
                            etkinlikToEdit={etkinlikToEdit}
                            kulup={kulup} // Kulüp bilgisini forma ilet
                            onSave={handleFormSave}
                            onCancel={() => setIsFormOpen(false)}
                        />
                    </div>
                </div>
            )}

            {renderTable(futureEvents, "📅 Gelecek Etkinlikler")}
            {renderTable(pastEvents, "📜 Geçmiş Etkinlikler")}

            {deletedEvents.length > 0 && (
                <div style={{ marginTop: '50px', borderTop: '2px dashed #bbb', paddingTop: '20px' }}>
                    {renderTable(deletedEvents, "🗑️ Silinmiş Etkinlikler", true)}
                </div>
            )}
        </div>
    );
};

export default EtkinlikYonetim;